import React from 'react';

const BAYER_8 = [
  [ 0, 32,  8, 40,  2, 34, 10, 42],
  [48, 16, 56, 24, 50, 18, 58, 26],
  [12, 44,  4, 36, 14, 46,  6, 38],
  [60, 28, 52, 20, 62, 30, 54, 22],
  [ 3, 35, 11, 43,  1, 33,  9, 41],
  [51, 19, 59, 27, 49, 17, 57, 25],
  [15, 47,  7, 39, 13, 45,  5, 37],
  [63, 31, 55, 23, 61, 29, 53, 21],
];

export function DitherCanvas({
  field,
  pixelSize = 3,
  fps = 18,
  fg = '#0A0A0A',
  bg = 'transparent',
  className = '',
  style = {},
  paused = false,
}) {
  const canvasRef = React.useRef(null);
  const rafRef = React.useRef(null);
  const startRef = React.useRef(performance.now());
  const lastDrawRef = React.useRef(0);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let alive = true;

    function resize() {
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(rect.width / pixelSize));
      canvas.height = Math.max(1, Math.floor(rect.height / pixelSize));
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const parseHex = (h) => {
      const m = h.replace('#', '');
      const v = m.length === 3
        ? m.split('').map(c => parseInt(c + c, 16))
        : [parseInt(m.slice(0,2),16), parseInt(m.slice(2,4),16), parseInt(m.slice(4,6),16)];
      return v;
    };
    const [fr, fg_, fb] = parseHex(fg);
    const isBgTransparent = bg === 'transparent';
    let br = 250, bg_ = 250, bb = 250;
    if (!isBgTransparent) [br, bg_, bb] = parseHex(bg);

    function tick(now) {
      if (!alive) return;
      const interval = 1000 / fps;
      if (now - lastDrawRef.current >= interval) {
        lastDrawRef.current = now;
        const t = (now - startRef.current) / 1000;
        const w = canvas.width;
        const h = canvas.height;
        const img = ctx.createImageData(w, h);
        const data = img.data;
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            const v = field(x, y, t, w, h);
            const threshold = (BAYER_8[y & 7][x & 7] + 0.5) / 64;
            const on = v > threshold;
            const i = (y * w + x) * 4;
            if (on) {
              data[i] = fr; data[i+1] = fg_; data[i+2] = fb; data[i+3] = 255;
            } else if (isBgTransparent) {
              data[i] = 0; data[i+1] = 0; data[i+2] = 0; data[i+3] = 0;
            } else {
              data[i] = br; data[i+1] = bg_; data[i+2] = bb; data[i+3] = 255;
            }
          }
        }
        ctx.putImageData(img, 0, 0);
      }
      if (!paused) rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      alive = false;
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [field, pixelSize, fps, fg, bg, paused]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        width: '100%',
        height: '100%',
        imageRendering: 'pixelated',
        display: 'block',
        ...style,
      }}
    />
  );
}

export const DitherFields = {
  sweep: (x, y, t, w, h) => {
    const nx = x / w;
    const ny = y / h;
    const wave = Math.sin(nx * 6 - t * 0.4) * 0.15 + Math.cos(ny * 4 + t * 0.2) * 0.1;
    return 1 - nx + wave;
  },
  radial: (x, y, t, w, h) => {
    const cx = w / 2, cy = h / 2;
    const dx = (x - cx) / w, dy = (y - cy) / h;
    const d = Math.sqrt(dx * dx + dy * dy);
    return Math.max(0, 1 - d * 2 + Math.sin(t * 0.5 + d * 8) * 0.15);
  },
  diagonal: (x, y, t, w, h) => {
    const v = (x + y) / (w + h);
    return 1 - v + Math.sin(v * 12 - t * 0.6) * 0.2;
  },
  horizon: (x, y, t, w, h) => {
    const ny = y / h;
    const nx = x / w;
    const noise = Math.sin(nx * 20 + t * 0.3) * 0.05 + Math.sin(ny * 15 - t * 0.4) * 0.05;
    return 1 - ny * 1.4 + noise;
  },
  vertical: (x, y, t, w, h) => {
    const ny = y / h;
    const nx = x / w;
    return 1 - ny + Math.sin(nx * 8 + t * 0.5) * 0.08;
  },
  plasma: (x, y, t, w, h) => {
    const nx = x / w * 4;
    const ny = y / h * 4;
    const v = Math.sin(nx + t * 0.3) * 0.5 + 0.5
            + Math.sin(ny + t * 0.4) * 0.3
            + Math.sin((nx + ny) * 0.5 + t * 0.2) * 0.2;
    return v / 1.5;
  },
  thinking: (x, y, t, w, h) => {
    const nx = x / w;
    const ny = y / h;
    const v = Math.sin(nx * 10 - t * 1.5) * 0.5 + Math.sin(ny * 8 + t * 1.2) * 0.5;
    return 0.5 + v * 0.3;
  },
};
